import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

const BAR_COUNT = 9;
const IDLE_SCALE = 0.12;

interface MicVisualizerProps {
  /** AnalyserNode from `useVoiceCapture`. Null while idle. */
  analyser: AnalyserNode | null;
  /** Tailwind className applied to the bar wrapper. */
  className?: string;
}

/**
 * Audio-reactive bar visualiser. Drives each bar's `transform:
 * scaleY(...)` from a slice of the analyser's frequency data on every
 * animation frame, bypassing React for performance — 60fps would
 * thrash a state-driven re-render. Bars use CSS transitions for
 * smoothing so the result reads as fluid even on quieter signals.
 *
 * Layout intentionally favours the MIDDLE bars (the energy bulge in
 * any natural voice signal sits in the mid frequencies) by ordering
 * bands non-linearly — the centre bars dance harder, which produces
 * the classic Siri / Apple Intelligence breathing pulse.
 */
export function MicVisualizer({ analyser, className }: MicVisualizerProps) {
  const barsRef = useRef<Array<HTMLSpanElement | null>>([]);

  useEffect(() => {
    if (!analyser) {
      // Drift the bars back to idle when the analyser is gone (mic
      // released). The CSS transition handles the actual easing.
      for (const bar of barsRef.current) {
        if (bar) bar.style.transform = `scaleY(${IDLE_SCALE})`;
      }
      return;
    }
    const data = new Uint8Array(analyser.frequencyBinCount);
    let raf = 0;
    let cancelled = false;

    // Non-linear band mapping: bar index 0 → low freq, centre →
    // mid (loudest for speech), outermost → high. We pre-compute the
    // start index for each bar so the inner loop stays tight.
    const half = (BAR_COUNT - 1) / 2;
    const bandStarts: number[] = [];
    const bandSizes: number[] = [];
    const bins = data.length;
    for (let i = 0; i < BAR_COUNT; i += 1) {
      // Centre bars draw from a tighter, lower-frequency window where
      // voice has more energy.
      const distFromCentre = Math.abs(i - half) / half; // 0 .. 1
      const start = Math.floor((distFromCentre * 0.55 + 0.02) * bins);
      const size = Math.max(2, Math.floor(bins * 0.08));
      bandStarts.push(Math.min(bins - size - 1, start));
      bandSizes.push(size);
    }

    function tick() {
      if (cancelled) return;
      analyser!.getByteFrequencyData(data);
      const bars = barsRef.current;
      for (let i = 0; i < BAR_COUNT; i += 1) {
        const bar = bars[i];
        if (!bar) continue;
        const start = bandStarts[i]!;
        const size = bandSizes[i]!;
        let sum = 0;
        for (let j = 0; j < size; j += 1) {
          sum += data[start + j] ?? 0;
        }
        const avg = sum / size / 255; // 0..1
        // Punch the response with a mild exponential so quiet speech
        // still produces visible movement, then clamp.
        const eased = Math.min(1, Math.pow(avg, 0.7) * 1.35);
        const scale = Math.max(IDLE_SCALE, eased);
        bar.style.transform = `scaleY(${scale.toFixed(3)})`;
      }
      raf = requestAnimationFrame(tick);
    }

    raf = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [analyser]);

  return (
    <div
      className={cn('flex h-12 items-center justify-center gap-1.5', className)}
      aria-hidden
    >
      {Array.from({ length: BAR_COUNT }, (_, i) => (
        <span
          key={i}
          ref={(el) => {
            barsRef.current[i] = el;
          }}
          className="h-full w-1.5 origin-center rounded-full bg-gradient-to-b from-accent via-accent to-accent/70 shadow-[0_0_8px_rgba(0,0,0,0.05)]"
          style={{
            transform: `scaleY(${IDLE_SCALE})`,
            transition: 'transform 90ms ease-out',
          }}
        />
      ))}
    </div>
  );
}
